import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import UsuariosService from "../../service/usuario";
import { stopTokenRefreshInterval } from "../../service/tokenRefresh";

const DropdownMenu = styled.div`
  position: fixed;
  top: 80px;
  right: 16px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 980;
  min-width: 250px;
  max-width: 320px;
  padding: 12px;
  font-family: "Roboto", sans-serif;
  display: ${(props) => (props.isOpen ? "block" : "none")};
  box-sizing: border-box;
  @media (max-width: 767px) {
    left: 12px;
    right: 12px;
    width: auto;
  }
`;

const InfoItem = styled.div`
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.div`
  font-family: "Roboto", sans-serif;
  font-size: 12px;
  color: #8e8e93;
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  color: #222;
`;

const PrivilegeGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 4px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  color: #222;
`;

const CheckboxInput = styled.input`
  width: 16px;
  height: 16px;
`;

const SelectorContainer = styled.div`
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 6px 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  box-sizing: border-box;
`;

const OptionsList = styled.div`
  position: relative;
  margin-top: 6px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  max-height: 160px;
  overflow: auto;
  z-index: 990;
`;

const OptionItem = styled.div`
  padding: 8px 10px;
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  color: #222;
  cursor: pointer;
  &:hover { background: #f5f5f5; }
`;

const SelectedList = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const SelectedPill = styled.span`
  background: #e6f7f7;
  color: #006b6b;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 13px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  padding-top: ${(props) => (props.isEditing ? "0" : "16px")};
  border-top: ${(props) => (props.isEditing ? "none" : "1px solid #eee")};
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
`;

const EditButton = styled(Button)`
  background-color: #008080;
  color: white;
  border: none;
  flex: 1;
`;

const LogoutButton = styled(Button)`
  background-color: #008080;
  color: white;
  border: none;
  flex: 1;
`;

const Input = styled.input`
  width: 100%;
  padding: 6px 10px;
  box-sizing: border-box;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  color: #222;

  &:focus {
    outline: none;
    border-color: #008080;
  }
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 8px 10px;
  box-sizing: border-box;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  color: #222;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #008080;
  }
`;

const SaveButton = styled(Button)`
  background-color: #008080;
  color: white;
  border: none;
  margin-top: 8px;
  width: 100%;
  padding: 8px 16px;
  border-radius: 4px;
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
`;

const ErrorMessage = styled.div`
  background-color: #fee;
  color: #c33;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  margin-bottom: 12px;
  border: 1px solid #fcc;
  font-family: "Roboto", sans-serif;
`;

const UserDropdown = ({ isOpen, onClose, usuario, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [editedUser, setEditedUser] = useState({
    nome: "",
    apelido: "",
    email: "",
    telefone: "",
    privilegio: [],
  });
  const [savedUsuario, setSavedUsuario] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setIsEditing(false);
      setErrorMessage("");
      setSavedUsuario(null);
      const publicadorCheck = Array.isArray(usuario?.perfil) && usuario.perfil.includes('publicador') ? ['publicador'] : [];
      const privilegiosCheck = Array.isArray(usuario?.privilegio) ? usuario.privilegio : (usuario?.privilegio ? [usuario.privilegio] : []);
      setEditedUser({
        nome: usuario?.nome || "",
        apelido: usuario?.apelido || "",
        email: usuario?.email || "",
        telefone: usuario?.telefone || "",
        privilegio: [...publicadorCheck, ...privilegiosCheck],
      });
    }
  }, [isOpen, usuario]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        !event.target.closest("[data-dropdown-container]") &&
        !event.target.closest("[data-dropdown-trigger]")
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const privilegiosPossiveis = [
    { value: 'publicador', label: 'Publicador' },
    { value: 'anciao', label: 'Ancião' },
    { value: 'servo-ministerial', label: 'Servo Ministerial' },
    { value: 'pioneiro-regular', label: 'Pioneiro Regular' },
  ];

  const privilegioLabel = (p) => {
    const map = {
      publicador: 'Publicador',
      anciao: 'Ancião',
      'servo-ministerial': 'Servo Ministerial',
      'pioneiro-regular': 'Pioneiro Regular',
    };
    return map[p] || p;
  };

  const handleEdit = () => {
    setIsEditing(true);
    setErrorMessage("");
    const publicadorCheck = Array.isArray(usuario?.perfil) && usuario.perfil.includes('publicador') ? ['publicador'] : [];
    const privilegiosCheck = Array.isArray(usuario?.privilegio) ? usuario.privilegio : (usuario?.privilegio ? [usuario.privilegio] : []);
    setEditedUser({
      nome: usuario?.nome || "",
      apelido: usuario?.apelido || "",
      email: usuario?.email || "",
      telefone: usuario?.telefone || "",
      privilegio: [...publicadorCheck, ...privilegiosCheck],
    });
  };

  const handlePrivilegioChange = (e) => {
    const { name, checked } = e.target;
    setEditedUser((prev) => {
      const prev_ = Array.isArray(prev.privilegio) ? prev.privilegio : [];
      const next = checked
        ? Array.from(new Set([...prev_, name]))
        : prev_.filter((p) => p !== name);
      return { ...prev, privilegio: next };
    });
  };


  const handleSave = async () => {
    try {
      setErrorMessage("");

      if (!editedUser.nome || !editedUser.email || !editedUser.telefone) {
        setErrorMessage("Por favor, preencha todos os campos");
        return;
      }

      const userId = localStorage.getItem("idUsuario");
      const selected = editedUser.privilegio || [];

      // publicador belongs to perfil field — keep other existing roles intact
      const hiddenRoles = Array.isArray(usuario?.perfil)
        ? usuario.perfil.filter((p) => p !== 'publicador')
        : [];
      const newPerfil = selected.includes('publicador')
        ? [...hiddenRoles, 'publicador']
        : hiddenRoles;

      // anciao/servo-ministerial/pioneiro-regular belong to privilegio field
      const newPrivilegio = selected.filter((p) => p !== 'publicador');

      const updateData = {
        nome: editedUser.nome,
        apelido: editedUser.apelido,
        email: editedUser.email,
        telefone: editedUser.telefone,
        perfil: newPerfil,
        privilegio: newPrivilegio,
      };

      const usuarioAtualizado = await UsuariosService.updateUsuario(userId, updateData);
      if (usuarioAtualizado?.apelido?.trim() !== editedUser.apelido.trim()) {
        setErrorMessage("A API não confirmou o apelido. Verifique se a versão atual da API foi implantada.");
        return;
      }

      setSavedUsuario(usuarioAtualizado);
      setIsEditing(false);
      setErrorMessage("");

      if (onUpdate) await onUpdate(usuarioAtualizado);
    } catch (error) {
      console.error("Erro ao atualizar usuário:", error);
      setErrorMessage("Erro ao atualizar os dados. Tente novamente.");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogout = () => {
    // Para a verificação periódica do token
    stopTokenRefreshInterval();
    
    // Limpa todos os dados do localStorage
    localStorage.removeItem("idUsuario");
    localStorage.removeItem("token");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("userInfo");
    try { localStorage.removeItem('perfilUsuario'); } catch (e) {}
    
    // Redireciona para a página de login
    navigate('/');
  };

  const currentUsuario = savedUsuario ? { ...usuario, ...savedUsuario } : usuario;

  // Combine publicador (from perfil) + anciao/servo/pioneiro (from privilegio) for display
  const publicadorDisplay = (currentUsuario?.perfil || []).includes('publicador') ? ['publicador'] : [];
  const privilegioDisplay = Array.isArray(currentUsuario?.privilegio)
    ? currentUsuario.privilegio
    : currentUsuario?.privilegio ? [currentUsuario.privilegio] : [];
  const allPrivilegios = [...publicadorDisplay, ...privilegioDisplay];

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <DropdownMenu isOpen={isOpen} data-dropdown-container>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      <InfoItem>
        <InfoLabel>Nome:</InfoLabel>
        {isEditing ? (
          <Input
            name="nome"
            value={editedUser.nome}
            onChange={handleInputChange}
          />
        ) : (
          <InfoValue>{usuario?.nome || "Não disponível"}</InfoValue>
        )}
      </InfoItem>
      <InfoItem>
        <InfoLabel>Apelido:</InfoLabel>
        {isEditing ? (
          <Input
            name="apelido"
            value={editedUser.apelido}
            onChange={handleInputChange}
          />
        ) : (
          <InfoValue>{currentUsuario?.apelido || "Não disponível"}</InfoValue>
        )}
      </InfoItem>
      <InfoItem>
        <InfoLabel>E-mail:</InfoLabel>
        {isEditing ? (
          <Input
            name="email"
            type="email"
            value={editedUser.email}
            onChange={handleInputChange}
          />
        ) : (
          <InfoValue>{usuario?.email || "Não disponível"}</InfoValue>
        )}
      </InfoItem>
      <InfoItem>
        <InfoLabel>Telefone:</InfoLabel>
        {isEditing ? (
          <Input
            name="telefone"
            value={editedUser.telefone}
            onChange={handleInputChange}
          />
        ) : (
          <InfoValue>{usuario?.telefone || "Não disponível"}</InfoValue>
        )}
      </InfoItem>
      <InfoItem>
        <InfoLabel>Privilégio:</InfoLabel>
        {isEditing ? (
          <PrivilegeGroup>
            {privilegiosPossiveis.map(({ value, label }) => (
              <CheckboxLabel key={value}>
                <CheckboxInput
                  type="checkbox"
                  name={value}
                  checked={(editedUser.privilegio || []).includes(value)}
                  onChange={handlePrivilegioChange}
                />
                {label}
              </CheckboxLabel>
            ))}
          </PrivilegeGroup>
        ) : (
          allPrivilegios.length > 0 ? (
            <SelectedList>
              {allPrivilegios.map((p) => (
                <SelectedPill key={p}>{privilegioLabel(p)}</SelectedPill>
              ))}
            </SelectedList>
          ) : (
            <InfoValue>Nenhum</InfoValue>
          )
        )}
      </InfoItem>
      <ButtonContainer isEditing={isEditing}>
        {isEditing ? (
          <SaveButton onClick={handleSave}>Salvar</SaveButton>
        ) : (
          <>
            <EditButton onClick={handleEdit}>Editar</EditButton>
            <LogoutButton onClick={handleLogout}>Sair</LogoutButton>
          </>
        )}
      </ButtonContainer>
    </DropdownMenu>,
    document.body
  );
};

export default UserDropdown;
